// custom hook to get EMI data for selected variant and city for the EmiCalculator popup

"use client";

import { useState, useEffect, useMemo } from "react";
import {
    calculateEMI,
    EMI_DEFAULT_INTEREST_RATE,
    EMI_DEFAULT_TENURE,
    formatEmiCurrency,
    getDefaultDownPayment,
} from "@/lib/utils/emi";
import { getOnRoadPrice } from "@/utils/onRoadPrice";
import type { CityData } from "@/types/city";
import { getCityBySlug, getGlobalSettings } from "@/app/utilities/apis";
import { getLocationCookie } from "@/app/utilities/locationCookie";

export interface EmiData {
    amount: string;
    tooltip: string;
}

export interface UseEmiDataOptions {
    model: any;
    selectedVariant: any;
    variants: any[];
    selectedCity: string;
    isPriceInCityRoute?: boolean;
    rtoResult: any;
    insuranceResult: any;
    isRtoLoading: boolean;
    isInsuranceLoading: boolean;
    price: number;
}

export interface UseEmiDataReturn {
    emiData: EmiData | null;
    isEmiOpen: boolean;
    emiSelectedVariant: any;
    emiOnRoadPrices: Record<string, number>;
    handleOpenEmiCalculator: () => void;
    setIsEmiOpen: (open: boolean) => void;
    setEmiSelectedVariant: (variant: any) => void;
}

export function useEmiData({
    model,
    selectedVariant,
    variants,
    selectedCity,
    isPriceInCityRoute = false,
    rtoResult,
    insuranceResult,
    price,
}: UseEmiDataOptions): UseEmiDataReturn {
    const [isEmiOpen, setIsEmiOpen] = useState(false);
    const [emiSelectedVariant, setEmiSelectedVariant] = useState<any>(selectedVariant);
    const [emiOnRoadPrices, setEmiOnRoadPrices] = useState<Record<string, number>>({});

    const hasOnRoadCharges = Boolean(rtoResult || insuranceResult);
    const emiCardPrice = Number(selectedVariant?.price || price) + (rtoResult?.total || 0) + (insuranceResult?.total || 0);

    const handleOpenEmiCalculator = () => {
        setEmiSelectedVariant(selectedVariant);
        setIsEmiOpen(true);
    };

    const fetchCityData = async (): Promise<CityData | null> => {
        const loc = getLocationCookie ? getLocationCookie() : null;
        if (loc?.slug) {
            const cityData = await getCityBySlug(loc.slug).catch(() => null);
            if (cityData) return cityData;
        }
        if (model?.locationInfo?.state?.rtoSlabs) return model.locationInfo;
        try {
            const def = await getGlobalSettings();
            return def || null;
        } catch {
            return null;
        }
    };

    // Compute on-road prices for all variants (used by EmiCalculator popup)
    useEffect(() => {
        let mounted = true;

        const computeEmiOnRoadPrices = async () => {
            try {
                const shouldCalculateCharges = Boolean(selectedCity) || isPriceInCityRoute;

                if (!shouldCalculateCharges || variants.length === 0) {
                    setEmiOnRoadPrices({});
                    return;
                }

                const cityData = await fetchCityData();
                if (!mounted || !cityData) {
                    setEmiOnRoadPrices({});
                    return;
                }

                const nextPrices = variants.reduce((acc: Record<string, number>, item: any) => {
                    const onRoadPrice = getOnRoadPrice(item, cityData)?.onRoadPrice ?? null;
                    if (item?._id && onRoadPrice) {
                        acc[item._id] = onRoadPrice;
                    }
                    return acc;
                }, {});

                setEmiOnRoadPrices(nextPrices);
            } catch {
                if (!mounted) return;
                setEmiOnRoadPrices({});
            }
        };

        computeEmiOnRoadPrices();
        return () => { mounted = false; };
    }, [variants, isPriceInCityRoute, selectedCity, model?.locationInfo?.name]);

    // Compute the EMI card display data (amount + tooltip)
    const emiData = useMemo((): EmiData | null => {
        if (emiCardPrice <= 0) return null;
        const downPayment = getDefaultDownPayment(emiCardPrice);
        const emi = calculateEMI({
            price: emiCardPrice,
            downPayment,
            interestRate: EMI_DEFAULT_INTEREST_RATE,
            tenureMonths: EMI_DEFAULT_TENURE,
        }).emi;
        const formattedRtoPrice = rtoResult?.total > 0 ? formatEmiCurrency(rtoResult.total) : null;
        const formattedInsurancePrice = insuranceResult?.total > 0 ? formatEmiCurrency(insuranceResult.total) : null;
        const locationText = hasOnRoadCharges && selectedCity ? ` for ${selectedCity}` : "";
        const priceBasisText = hasOnRoadCharges ? `on-road price${locationText}` : "ex-showroom price";
        const rtoText = formattedRtoPrice ? ` Includes RTO of ₹${formattedRtoPrice}.` : "";
        const insuranceText = formattedInsurancePrice ? ` Includes insurance of ₹${formattedInsurancePrice}.` : "";
        return {
            amount: formatEmiCurrency(emi),
            tooltip: `Calculated on ${priceBasisText} with 10% down payment (₹${formatEmiCurrency(downPayment)}), ${EMI_DEFAULT_INTEREST_RATE}% interest, and ${EMI_DEFAULT_TENURE} months tenure.${rtoText}${insuranceText}`,
        };
    }, [emiCardPrice, hasOnRoadCharges, insuranceResult, selectedCity, rtoResult]);

    return {
        emiData,
        isEmiOpen,
        emiSelectedVariant,
        emiOnRoadPrices,
        handleOpenEmiCalculator,
        setIsEmiOpen,
        setEmiSelectedVariant,
    };
}
